import { z } from 'zod';

import { FILE_OVERVIEW_SECTIONS, type FileOverviewContentSectionKey } from './Files.ts';

// ─── Event types, contexts, review statuses, operations ─────────────────────

export const POPULATION_CRITERIA_TREES = {
  RECOMMENDATIONS: 'recommendations',
  DENOMINATOR: 'denominator'
} as const;

export type PopulationCriteriaTreeKey =
  (typeof POPULATION_CRITERIA_TREES)[keyof typeof POPULATION_CRITERIA_TREES];

/** Stable iteration order for the two trees, matching the overview sections they come from. */
export const POPULATION_CRITERIA_TREE_ORDER = [
  POPULATION_CRITERIA_TREES.RECOMMENDATIONS,
  POPULATION_CRITERIA_TREES.DENOMINATOR
] as const satisfies readonly PopulationCriteriaTreeKey[];

/**
 * The pathway-overview section each tree is generated from, and so the section whose
 * edits can drift that tree. Every surface that ties the two together reads this:
 * the hash/description selector, the author's per-section drift badge, and the
 * criteria sidebar.
 */
export const POPULATION_CRITERIA_TREE_OVERVIEW_SECTIONS = {
  [POPULATION_CRITERIA_TREES.RECOMMENDATIONS]: FILE_OVERVIEW_SECTIONS.INCLUSION,
  [POPULATION_CRITERIA_TREES.DENOMINATOR]: FILE_OVERVIEW_SECTIONS.OUTCOMES_POPULATION
} as const satisfies Record<PopulationCriteriaTreeKey, FileOverviewContentSectionKey>;

/** Inverse of POPULATION_CRITERIA_TREE_OVERVIEW_SECTIONS: the tree a section feeds, if any. */
export const POPULATION_CRITERIA_OVERVIEW_SECTION_TREES = {
  [FILE_OVERVIEW_SECTIONS.INCLUSION]: POPULATION_CRITERIA_TREES.RECOMMENDATIONS,
  [FILE_OVERVIEW_SECTIONS.OUTCOMES_POPULATION]: POPULATION_CRITERIA_TREES.DENOMINATOR
} as const;

export const POPULATION_CRITERIA_EVENT_TYPES = {
  AI_INITIAL: 'ai_initial',
  AI_REANALYSIS: 'ai_reanalysis',
  AI_AUTO_REGENERATION: 'ai_auto_regeneration',
  MANUAL_EDIT: 'manual_edit',
  RESTORE: 'restore',
  PROMOTE: 'promote'
} as const;

export const POPULATION_CRITERIA_CONTEXTS = {
  EDIT: 'edit',
  LIVE: 'live'
} as const;

export const POPULATION_CRITERIA_REVIEW_STATUSES = {
  NEEDS_REVIEW: 'needs_review',
  IN_PROGRESS: 'in_progress',
  APPROVED: 'approved'
} as const;

// Verdict of the last completed check of a tree's criteria against the
// overview section they were generated from.
export const POPULATION_CRITERIA_OVERVIEW_STATUSES = {
  CURRENT: 'current',
  NEEDS_UPDATE: 'needs_update'
} as const;

export type PopulationCriteriaOverviewStatus =
  (typeof POPULATION_CRITERIA_OVERVIEW_STATUSES)[keyof typeof POPULATION_CRITERIA_OVERVIEW_STATUSES];

// A tree's pending regeneration. The record is deleted once its criteria are current.
export const POPULATION_CRITERIA_REGENERATION_STATES = {
  CHECKING: 'checking',
  QUEUED: 'queued',
  GENERATING: 'generating',
  FAILED: 'failed'
} as const;

// An overview edit is checked once it has been quiet this long, and regenerated once quiet for the
// generation delay.
export const POPULATION_CRITERIA_REGENERATION_CHECK_DELAY_MS = 3_000;
export const POPULATION_CRITERIA_REGENERATION_GENERATION_DELAY_MS = 5 * 60_000;

export type PopulationCriteriaRegenerationState =
  (typeof POPULATION_CRITERIA_REGENERATION_STATES)[keyof typeof POPULATION_CRITERIA_REGENERATION_STATES];

// A trial run of an overview edit: generated without saving, then applied as-is if accepted.
export const POPULATION_CRITERIA_PROPOSAL_STATES = {
  PENDING: 'pending',
  GENERATING: 'generating',
  READY: 'ready',
  ACCEPTED: 'accepted',
  FAILED: 'failed'
} as const;

export type PopulationCriteriaProposalState =
  (typeof POPULATION_CRITERIA_PROPOSAL_STATES)[keyof typeof POPULATION_CRITERIA_PROPOSAL_STATES];

export const POPULATION_CRITERIA_OPERATIONS = {
  INITIAL: 'initial',
  REANALYSIS: 'reanalysis'
} as const;

export const POPULATION_CRITERIA_QUEUE_OPERATIONS = {
  INITIAL: 'initial',
  REANALYSIS_DRAFT: 'reanalysis-draft',
  REANALYSIS_PROMOTE: 'reanalysis-promote',
  // Promote the existing EDIT criteria to LIVE with no regeneration — publishes
  // drafts that were generated but never promoted (the engine only reads LIVE).
  PROMOTE_ONLY: 'promote-only'
} as const;

export const RESOLVED_DATA_TYPES = {
  DIAGNOSIS: 'diagnosis',
  CHIEF_COMPLAINT: 'chiefComplaint',
  LAB_VALUE: 'labValue',
  DEVICE: 'device',
  SETTING: 'setting',
  DEMOGRAPHICS: 'demographics',
  MEDICATION: 'medication',
  PROCEDURE: 'procedure',
  VITAL: 'vital',
  ALLERGY: 'allergy',
  // Qualitative result observation (e.g. a positive blood culture). Distinct
  // from labValue (numeric threshold): the signal is the result's POSITIVITY,
  // not a number. Matched by test code (LOINC) or display keyword, gated on a
  // positive/abnormal result (extractor-computed). See extractResultObservations.
  OBSERVATION_RESULT: 'observationResult'
} as const;

// The channel a launch fact is recorded on in the warehouse.
export const FACT_CHANNELS = {
  CHIEF_COMPLAINT: 'chiefComplaint',
  DIAGNOSIS: 'diagnosis',
  MEDICATION: 'medication',
  PROCEDURE: 'procedure',
  LAB: 'lab',
  VITAL: 'vital',
  RESULT: 'result',
  DEVICE: 'device',
  ALLERGY: 'allergy'
} as const;

export type FactChannel = (typeof FACT_CHANNELS)[keyof typeof FACT_CHANNELS];

// Criterion types matched by code, and the channel their codes are counted on. Medications and allergies match on display.
export const CODE_USAGE_CHANNELS: Partial<Record<string, FactChannel>> = {
  [RESOLVED_DATA_TYPES.DIAGNOSIS]: FACT_CHANNELS.DIAGNOSIS,
  [RESOLVED_DATA_TYPES.CHIEF_COMPLAINT]: FACT_CHANNELS.CHIEF_COMPLAINT,
  [RESOLVED_DATA_TYPES.LAB_VALUE]: FACT_CHANNELS.LAB,
  [RESOLVED_DATA_TYPES.VITAL]: FACT_CHANNELS.VITAL,
  [RESOLVED_DATA_TYPES.OBSERVATION_RESULT]: FACT_CHANNELS.RESULT,
  [RESOLVED_DATA_TYPES.PROCEDURE]: FACT_CHANNELS.PROCEDURE
};

// Gate WHO is measured rather than describe a finding, so they carry no matching evidence alone.
export const CRITERIA_GATE_TYPES = [
  RESOLVED_DATA_TYPES.DEMOGRAPHICS,
  RESOLVED_DATA_TYPES.SETTING
] as const;

export type CriteriaGateType = (typeof CRITERIA_GATE_TYPES)[number];

// WHERE in the chart a diagnosis has to appear, which is the distinction the
// FHIR data actually draws: Condition.category separates this visit's diagnosis
// list from the patient's longitudinal problem list, and the two are fetched by
// separate searches. `encounter` = a diagnosis of THIS visit (acute target
// conditions the pathway treats). `problemList` = on the problem list, however
// old (chronic conditions, comorbidities, "history of"). `any` = either.
// Absent = `any`.
export const DIAGNOSIS_SCOPE = {
  ENCOUNTER: 'encounter',
  PROBLEM_LIST: 'problemList',
  ANY: 'any'
} as const;

export type DiagnosisScope = (typeof DIAGNOSIS_SCOPE)[keyof typeof DIAGNOSIS_SCOPE];

// Superseded by DIAGNOSIS_SCOPE. Criteria authored before the rename persist
// `temporality: 'current' | 'any'` in JSONB; `diagnosisScopeOf` maps them.
export const LEGACY_DIAGNOSIS_TEMPORALITY = {
  CURRENT: 'current',
  ANY: 'any'
} as const;

export type LegacyDiagnosisTemporality =
  (typeof LEGACY_DIAGNOSIS_TEMPORALITY)[keyof typeof LEGACY_DIAGNOSIS_TEMPORALITY];

/**
 * The scope a diagnosis criterion is evaluated at, tolerating criteria authored
 * before the `temporality` → `scope` rename. Legacy `current` becomes
 * `encounter`: it meant this visit's diagnosis plus a 7-day problem-list
 * allowance that no longer exists.
 *
 * Every read of the scope must go through here — the matcher evaluates
 * `resolvedData` straight off persisted JSONB without a Zod parse, so schema
 * defaults alone would not cover it.
 */
export function diagnosisScopeOf(rd: {
  scope?: DiagnosisScope;
  temporality?: LegacyDiagnosisTemporality;
}): DiagnosisScope {
  if (rd.scope) {
    return rd.scope;
  }

  return rd.temporality === LEGACY_DIAGNOSIS_TEMPORALITY.CURRENT
    ? DIAGNOSIS_SCOPE.ENCOUNTER
    : DIAGNOSIS_SCOPE.ANY;
}

// How a procedure criterion treats timing. `planned` = ordered/scheduled for a
// future date (active ProcedureRequest); `active` = ordered on this encounter
// but not yet performed; `performed` = performed historically (completed
// Procedure). Absent = `performed`, preserving the pre-temporality
// performed-only meaning of existing criteria.
export const PROCEDURE_TEMPORALITY = {
  PLANNED: 'planned',
  ACTIVE: 'active',
  PERFORMED: 'performed'
} as const;

export type ProcedureTemporality =
  (typeof PROCEDURE_TEMPORALITY)[keyof typeof PROCEDURE_TEMPORALITY];

// Short human labels for compact display (badges). The verbose,
// self-explaining option text lives with the editor dropdown.
export const TEMPORALITY_LABELS: Record<ProcedureTemporality, string> = {
  [PROCEDURE_TEMPORALITY.PLANNED]: 'Planned',
  [PROCEDURE_TEMPORALITY.ACTIVE]: 'This encounter',
  [PROCEDURE_TEMPORALITY.PERFORMED]: 'Performed'
};

export const DIAGNOSIS_SCOPE_LABELS: Record<DiagnosisScope, string> = {
  [DIAGNOSIS_SCOPE.ENCOUNTER]: 'This encounter',
  [DIAGNOSIS_SCOPE.PROBLEM_LIST]: 'Problem list',
  [DIAGNOSIS_SCOPE.ANY]: 'Any'
};

// ─── Usage suggestions ──────────────────────────────────────────────────────

/** The edits the warehouse suggests to a flowchart's criteria from how clinicians used it. */
export const SUGGESTION_EDIT_KINDS = {
  REQUIRE: 'require',
  EXCLUDE: 'exclude',
  ADD: 'add',
  REMOVE: 'remove',
  EXTEND: 'extend'
} as const;

export type SuggestionEditKind = (typeof SUGGESTION_EDIT_KINDS)[keyof typeof SUGGESTION_EDIT_KINDS];

/** Why a flowchart has the suggestions it has, so an empty list never reads as a bug. */
export const SUGGESTION_AVAILABILITY = {
  READY: 'ready',
  NO_IMPROVEMENT: 'no_improvement',
  NEVER_MATCHED: 'never_matched',
  FEW_PATHWAY_USES: 'few_pathway_uses',
  NO_DATA: 'no_data'
} as const;

export type SuggestionAvailabilityReason =
  (typeof SUGGESTION_AVAILABILITY)[keyof typeof SUGGESTION_AVAILABILITY];

export const CRITERIA_OPERATORS = {
  AND: 'AND',
  OR: 'OR'
} as const;

export const CRITERION_TYPES = {
  GROUP: 'group'
} as const;

export const CRITERION_SOURCES = {
  AI: 'ai',
  HUMAN: 'human'
} as const;

export type CriterionSource = (typeof CRITERION_SOURCES)[keyof typeof CRITERION_SOURCES];

// How the denominator query ended up enforcing a criterion, reported per criterion
// id so consumers don't have to re-derive the query builder's lifting rules.
// `match` emitted a match CTE, so the criterion appears in an encounter's
// `matched_criteria`. `always` was lifted to a hard filter (a top-level
// conjunctive demographics WHERE, or the setting gate that prunes the base scan),
// so every returned encounter satisfies it and it never appears in
// `matched_criteria`. `omitted` never reached the query at all — disabled,
// unresolved, or a setting with no values.
export const CRITERION_ENFORCEMENT = {
  MATCH: 'match',
  ALWAYS: 'always',
  OMITTED: 'omitted'
} as const;

export type CriterionEnforcement =
  (typeof CRITERION_ENFORCEMENT)[keyof typeof CRITERION_ENFORCEMENT];

export const COMPARISON_OPS = {
  GT: 'gt',
  GTE: 'gte',
  LT: 'lt',
  LTE: 'lte'
} as const;

// Logical operator on a ResolvedData's `values` / `comparisons` list. Authored
// by the AI resolver (see apps/api/src/services/populationCriteria/schemas.ts).
// `all_of` requires every value to match; `any_of` accepts the first hit.
export const RESOLVED_DATA_OPERATORS = {
  ALL_OF: 'all_of',
  ANY_OF: 'any_of'
} as const;

// Flagsmith key that gates the population-criteria recommendations engine.
// Enabled = visible in the view layer; see
// apps/org/src/services/recommendations/populationCriteria.ts for parsing.
export const POPULATION_CRITERIA_RECS_FEATURE_FLAG = 'org-recommendations-population-criteria';

// ─── Code system enum + per-system format regex ─────────────────────────────

export const CODE_SYSTEM_VALUES = [
  'ICD-10-CM',
  'ICD-10-PCS',
  'SNOMED-CT',
  'LOINC',
  'RxNorm',
  'CPT'
] as const;

export const CodeSystem = z.enum(CODE_SYSTEM_VALUES);
export type CodeSystemValue = (typeof CODE_SYSTEM_VALUES)[number];

/**
 * Format regex per code system. Structural validation only — "the string
 * looks like a X code." Existence is validated separately against the
 * terminology server.
 */
const CODE_FORMAT: Record<CodeSystemValue, RegExp> = {
  // ICD-10-CM: letter prefix; optional up to 4 extension chars after the dot.
  // e.g. I50.9, U07.1, E11.65, Z00.00, C92.11
  'ICD-10-CM': /^[A-Z][0-9][0-9A-Z](\.[0-9A-Z]{1,4})?$/,
  // ICD-10-PCS: 7 alphanumeric characters, no dots. Excludes I/O (ambiguous with 1/0).
  'ICD-10-PCS': /^[0-9A-HJ-NP-Z]{7}$/,
  // SNOMED CT: numeric concept id, 6–18 digits.
  'SNOMED-CT': /^[0-9]{6,18}$/,
  // LOINC: N-M format (e.g. 2708-6). Up to 7 digits before the hyphen.
  LOINC: /^[0-9]{1,7}-[0-9]$/,
  // RxNorm: numeric RxCUI.
  RxNorm: /^[0-9]+$/,
  // CPT: 5-digit category I, or 4-digit + letter category II/III.
  CPT: /^[0-9]{5}$|^[0-9]{4}[A-Z]$/
};

/**
 * Detects a range-shaped string like `I05-I09`, `I05.0-I05.9`, or `36.1-36.2`.
 * Each side must be either (a) a letter-prefixed ICD-style token (optionally
 * with a `.suffix`), or (b) a decimal-dotted numeric like `36.1`. This shape
 * keeps LOINC codes (e.g. `2708-6`) — bare digits joined by a hyphen — from
 * matching, since LOINC is legal and is rejected as a range only when the
 * intent is clearly ICD-range syntax.
 */
const CODE_RANGE_PATTERN =
  /^([A-Za-z][A-Z0-9]*(\.[0-9A-Z]+)?|[0-9]+\.[0-9]+)-([A-Za-z][A-Z0-9]*(\.[0-9A-Z]+)?|[0-9]+\.[0-9]+)$/;

// ─── Value schemas ──────────────────────────────────────────────────────────

/**
 * Terminology expansion set for a coded value or comparison: the codes that
 * count as a hit for subsumption matching (is-a descendants for SNOMED-CT /
 * ICD-10-CM, group members for LOINC). Populated by the expansion layer
 * (`annotateCodeExpansions` in @agilemd/population-criteria), never by
 * authors or generation — generation's AI schemas do not include it.
 */
const ExpandedCodesSchema = z.array(z.string()).optional();

/**
 * Coded value with strict system + per-system format validation.
 * Used for criterion types that drive query generation by code:
 * diagnosis (ICD-10-CM), medication (RxNorm), procedure (CPT / ICD-10-PCS).
 */
export const CodedValueSchema = z
  .object({
    display: z.string().min(1),
    code: z
      .string()
      .min(1)
      .refine(c => !CODE_RANGE_PATTERN.test(c), {
        message: 'Code ranges (e.g. I05-I09) are not allowed; enumerate codes'
      }),
    system: CodeSystem,
    expandedCodes: ExpandedCodesSchema
  })
  .superRefine((v, ctx) => {
    const pattern = CODE_FORMAT[v.system];

    if (!pattern.test(v.code)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['code'],
        message: `Code "${v.code}" is not a valid ${v.system} code format`
      });
    }
  });

/**
 * Text value with optional coded hints. Used for criterion types whose
 * query-build path matches on display strings rather than codes:
 * chiefComplaint, device, allergy, observationResult. The LLM may still emit
 * `system`/`code` — we don't enforce them here because they don't drive
 * query generation.
 */
export const TextValueSchema = z.object({
  display: z.string().min(1),
  code: z.string().optional(),
  system: z.string().optional(),
  expandedCodes: ExpandedCodesSchema
});

/**
 * Setting location code. Normalized from free-form text ("Emergency
 * Department", "ER") to the canonical lowercase tag via a preprocess step.
 */
export const VALID_SETTING_CODES = ['ed', 'inpatient', 'ambulatory', 'icu'] as const;

const SETTING_ALIASES: Record<string, (typeof VALID_SETTING_CODES)[number]> = {
  'emergency department': 'ed',
  emergency: 'ed',
  er: 'ed',
  'intensive care unit': 'icu',
  'intensive care': 'icu',
  office: 'ambulatory',
  outpatient: 'ambulatory',
  clinic: 'ambulatory'
};

function normalizeSettingCode(code: string): string {
  const lower = code.toLowerCase();

  if ((VALID_SETTING_CODES as readonly string[]).includes(lower)) {
    return lower;
  }

  return SETTING_ALIASES[lower] ?? lower;
}

export const SettingValueSchema = z
  .object({
    display: z.string().min(1),
    // Allow an empty or alias code on input; the transform below falls back
    // to `display` and normalizes ("Emergency Department" → "ed") before
    // the piped enum validation runs.
    code: z.string().optional(),
    system: z.string().optional()
  })
  .transform(v => ({ ...v, code: normalizeSettingCode(v.code || v.display) }))
  .pipe(
    z.object({
      display: z.string().min(1),
      code: z.enum(VALID_SETTING_CODES),
      system: z.string().optional()
    })
  );

/**
 * Lab or vital comparison: measurement with a numeric threshold and a unit.
 * System is LOINC for lab tests and basic vitals, or SNOMED-CT for clinical
 * assessment scores (CIWA, RASS, GCS, pain scales). Unit is a loose string at
 * the schema level; UCUM canonicalization + validation is layered on at the
 * save seam in core-content (ucum-lhc is a backend-only dep).
 */
const numericString = (label: string) =>
  z.string().refine(v => !Number.isNaN(parseFloat(v)), {
    message: `${label} must be a numeric string`
  });

export const ComparisonValueSchema = z
  .object({
    reference: z.string().min(1),
    code: z.string().min(1),
    system: z.enum(['LOINC', 'SNOMED-CT']),
    op: z.enum(['gt', 'lt', 'gte', 'lte']),
    value: numericString('value'),
    unit: z.string(),
    expandedCodes: ExpandedCodesSchema,
    // Absolute valence (ionic charge) of the measured substance — a property of
    // the analyte, NOT of `unit`. Set it for any ionic electrolyte (Na⁺/K⁺/Cl⁻/
    // HCO₃⁻ = 1, Ca²⁺/Mg²⁺ = 2) regardless of whether `unit` is molar or
    // equivalent: UCUM cannot convert equivalents ↔ moles (mEq/L ↔ mmol/L)
    // without it, and the EHR may report the lab in either family, so the matcher
    // needs it to convert the patient's value into `unit` in EITHER direction.
    // Omitted for non-ionic measures (osmolality, glucose, eGFR, …).
    charge: z.number().int().positive().optional()
  })
  .superRefine((v, ctx) => {
    if (!CODE_FORMAT[v.system].test(v.code)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['code'],
        message: `Code "${v.code}" is not a valid ${v.system} code format`
      });
    }
  });

// ─── Resolved-data variants ─────────────────────────────────────────────────

const Operator = z.enum(['any_of', 'all_of']);

/** Returns true when all `(system, code)` pairs in the array are unique. */
function dedupeValues(values: Array<{ code: string; system?: string }>): boolean {
  const seen = new Set<string>();
  for (const v of values) {
    const key = `${v.system ?? ''}|${v.code}`;

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
  }
  return true;
}

const dupeMessage = 'duplicate (system, code) pairs in values';

// Per-variant schemas are plain `ZodObject`s (no `.refine()`) so they're
// usable with `z.discriminatedUnion`, which in Zod 3 rejects refined
// variants. Cross-field constraints live in a single `.superRefine()` on
// the union below — this also keeps dedupe/presence logic in one place.
// Diagnoses come from FHIR Condition resources, which the matcher intersects
// against both ICD-10-CM and SNOMED-CT facts (see matcher/byType.ts), so a
// diagnosis criterion may carry codes from either system.
const DiagnosisValue = CodedValueSchema.refine(
  v => v.system === 'ICD-10-CM' || v.system === 'SNOMED-CT',
  { message: 'diagnosis values must use ICD-10-CM or SNOMED-CT' }
);

const RxNormValue = CodedValueSchema.refine(v => v.system === 'RxNorm', {
  message: 'medication values must use RxNorm'
});

// Procedures match by CPT/ICD-10-PCS code, by SNOMED-CT code (is-a expansion),
// or by display keyword (see matcher/byType.ts matchProcedure). Surgical
// procedures are frequently SNOMED-coded, so all three coded systems are valid.
const CptOrPcsValue = CodedValueSchema.refine(
  v => v.system === 'CPT' || v.system === 'ICD-10-PCS' || v.system === 'SNOMED-CT',
  { message: 'procedure values must use CPT, ICD-10-PCS, or SNOMED-CT' }
);

// UCUM time codes accepted on demographics ages. 'a' = year (default — Julian
// 365.25 d), 'mo' = month (1/12 a ≈ 30.4375 d), 'w' = week (7 d), 'd' = day.
// Authoring sub-year ages (neonates / infants) requires `mo`, `w`, or `d`;
// years rounds them to 0 and silently mismatches.
export const AGE_UNIT_VALUES = ['a', 'mo', 'w', 'd'] as const;
export type AgeUnit = (typeof AGE_UNIT_VALUES)[number];

export const AGE_UNIT_TO_DAYS: Record<AgeUnit, number> = {
  a: 365.25,
  mo: 30.4375,
  w: 7,
  d: 1
};

export const AGE_UNIT_LABELS: Record<AgeUnit, string> = {
  a: 'years',
  mo: 'months',
  w: 'weeks',
  d: 'days'
};

// UCUM age unit → DATE_DIFF grain. BigQuery, Snowflake, and DuckDB all
// accept these strings as the first arg to DATE_DIFF.
export const AGE_UNIT_TO_DATE_DIFF: Record<AgeUnit, 'year' | 'month' | 'week' | 'day'> = {
  a: 'year',
  mo: 'month',
  w: 'week',
  d: 'day'
};

// Per-unit upper bound. Years cap at 130 (matches prior behaviour); other
// units are derived so the absolute span is the same — keeps refines simple
// and prevents "Age ≤ 1000 years" via the days field.
export const AGE_UNIT_MAX: Record<AgeUnit, number> = {
  a: 130,
  mo: 1560,
  w: 6800,
  d: 47500
};

export function ageInDays(value: number, unit: AgeUnit | null | undefined): number {
  return value * AGE_UNIT_TO_DAYS[unit ?? 'a'];
}

export const DemographicsCriterionSchema = z.object({
  type: z.literal('demographics'),
  ageMin: z.coerce.number().int().min(0).max(50000).nullable().optional(),
  ageMax: z.coerce.number().int().min(0).max(50000).nullable().optional(),
  ageUnit: z.enum(AGE_UNIT_VALUES).nullable().optional(),
  gender: z.enum(['any', 'Male', 'Female']).optional()
});

export const DiagnosisCriterionSchema = z.object({
  type: z.literal('diagnosis'),
  operator: Operator.optional(),
  values: z.array(DiagnosisValue).min(1, { message: 'diagnosis must have at least one value' }),
  // Deliberately no `.default()`: defaulting here would write `scope: 'any'`
  // onto a pre-rename criterion and outrank its legacy `temporality: 'current'`,
  // silently widening it. `diagnosisScopeOf` owns the fallback.
  scope: z
    .enum([DIAGNOSIS_SCOPE.ENCOUNTER, DIAGNOSIS_SCOPE.PROBLEM_LIST, DIAGNOSIS_SCOPE.ANY])
    .optional()
    .describe(
      "Always set this. 'encounter' = the diagnosis must be on THIS visit's diagnosis list — use for an acute episode the pathway treats (hyperkalemia, sepsis, GI bleed). 'problemList' = on the patient's problem list, however old — use for chronic conditions, comorbidities and 'history of' qualifiers (heart failure, COPD, diabetes). 'any' = either, for statements that don't distinguish."
    ),
  // Superseded by `scope`. Declared so Zod does not strip it off a criterion
  // authored before the rename, which would silently widen 'current' to 'any'
  // on the next save. Read it via `diagnosisScopeOf`, never directly.
  temporality: z
    .enum([LEGACY_DIAGNOSIS_TEMPORALITY.CURRENT, LEGACY_DIAGNOSIS_TEMPORALITY.ANY])
    .optional()
});

// Encounter-scoped only: a problem-list diagnosis may be years old and would inflate a cohort count.
export const DenominatorDiagnosisCriterionSchema = z.object({
  type: z.literal('diagnosis'),
  operator: Operator.optional(),
  values: z.array(DiagnosisValue).min(1, { message: 'diagnosis must have at least one value' }),
  scope: z.literal(DIAGNOSIS_SCOPE.ENCOUNTER).optional()
});

export const MedicationCriterionSchema = z.object({
  type: z.literal('medication'),
  operator: Operator.optional(),
  values: z.array(RxNormValue).min(1, { message: 'medication must have at least one value' })
});

export const ProcedureCriterionSchema = z.object({
  type: z.literal('procedure'),
  operator: Operator.optional(),
  values: z.array(CptOrPcsValue).min(1, { message: 'procedure must have at least one value' }),
  temporality: z
    .enum([
      PROCEDURE_TEMPORALITY.PLANNED,
      PROCEDURE_TEMPORALITY.ACTIVE,
      PROCEDURE_TEMPORALITY.PERFORMED
    ])
    .default(PROCEDURE_TEMPORALITY.PERFORMED)
    .describe(
      "Always set this. 'planned' = ordered/scheduled for a future date (pre-op pathways). 'active' = ordered on this encounter but not yet performed (perioperative/active management). 'performed' = performed historically (post-op follow-up, complication pathways, surgical history)."
    )
});

export const LabValueCriterionSchema = z.object({
  type: z.literal('labValue'),
  operator: Operator.optional(),
  comparisons: z
    .array(ComparisonValueSchema)
    .min(1, { message: 'labValue must have at least one comparison' })
});

export const VitalCriterionSchema = z.object({
  type: z.literal('vital'),
  operator: Operator.optional(),
  comparisons: z
    .array(ComparisonValueSchema)
    .min(1, { message: 'vital must have at least one comparison' })
});

export const DeviceCriterionSchema = z.object({
  type: z.literal('device'),
  operator: Operator.optional(),
  values: z.array(TextValueSchema).min(1, { message: 'device must have at least one value' })
});

export const AllergyCriterionSchema = z.object({
  type: z.literal('allergy'),
  operator: Operator.optional(),
  values: z.array(TextValueSchema).min(1, { message: 'allergy must have at least one value' })
});

/**
 * Qualitative result observation: the patient has a result for one of the named
 * tests (by LOINC code or display keyword) and — when `requirePositive` (the
 * default) — that result is positive/abnormal (an organism grew, not "No
 * Growth"). Values carry the TEST identity (e.g. blood culture); positivity is
 * computed from the observation value by the extractor, not authored here.
 */
export const ObservationResultCriterionSchema = z.object({
  type: z.literal('observationResult'),
  operator: Operator.optional(),
  values: z
    .array(TextValueSchema)
    .min(1, { message: 'observationResult must have at least one value' }),
  requirePositive: z.boolean().optional()
});

export const ChiefComplaintCriterionSchema = z.object({
  type: z.literal('chiefComplaint'),
  operator: Operator.optional(),
  values: z
    .array(TextValueSchema)
    .min(1, { message: 'chiefComplaint must have at least one value' })
});

export const SettingCriterionSchema = z.object({
  type: z.literal('setting'),
  operator: Operator.optional(),
  values: z.array(SettingValueSchema).min(1, { message: 'setting must have at least one value' })
});

type RefinableResolvedData =
  | z.infer<typeof DemographicsCriterionSchema>
  | z.infer<typeof DiagnosisCriterionSchema>
  | z.infer<typeof DenominatorDiagnosisCriterionSchema>
  | z.infer<typeof MedicationCriterionSchema>
  | z.infer<typeof ProcedureCriterionSchema>
  | z.infer<typeof LabValueCriterionSchema>
  | z.infer<typeof VitalCriterionSchema>
  | z.infer<typeof DeviceCriterionSchema>
  | z.infer<typeof AllergyCriterionSchema>
  | z.infer<typeof ChiefComplaintCriterionSchema>
  | z.infer<typeof ObservationResultCriterionSchema>
  | z.infer<typeof SettingCriterionSchema>;

// Shared by both tree unions so they can't drift on what counts as valid.
function refineResolvedData(d: RefinableResolvedData, ctx: z.RefinementCtx): void {
  if (d.type === 'demographics') {
    const hasAge = d.ageMin != null || d.ageMax != null;
    const hasGender = d.gender != null && d.gender !== 'any';

    if (!hasAge && !hasGender) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'demographics must specify at least one of ageMin, ageMax, or non-any gender'
      });
    }

    const unit = d.ageUnit ?? 'a';
    const max = AGE_UNIT_MAX[unit];

    if (d.ageMin != null && d.ageMin > max) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['ageMin'],
        message: `ageMin must be ≤ ${max} for unit '${unit}'`
      });
    }

    if (d.ageMax != null && d.ageMax > max) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['ageMax'],
        message: `ageMax must be ≤ ${max} for unit '${unit}'`
      });
    }

    if (d.ageMin != null && d.ageMax != null && d.ageMin > d.ageMax) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['ageMin'],
        message: `ageMin (${d.ageMin}) must be ≤ ageMax (${d.ageMax}); both share ageUnit '${unit}'`
      });
    }

    return;
  }

  if (d.type === 'diagnosis' || d.type === 'medication' || d.type === 'procedure') {
    if (!dedupeValues(d.values)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['values'],
        message: dupeMessage
      });
    }

    return;
  }
}

export const RecommendationsResolvedDataSchema = z
  .discriminatedUnion('type', [
    DemographicsCriterionSchema,
    DiagnosisCriterionSchema,
    MedicationCriterionSchema,
    ProcedureCriterionSchema,
    LabValueCriterionSchema,
    VitalCriterionSchema,
    DeviceCriterionSchema,
    AllergyCriterionSchema,
    ChiefComplaintCriterionSchema,
    ObservationResultCriterionSchema,
    SettingCriterionSchema
  ])
  .superRefine(refineResolvedData);

// Retrospective cohort-membership evidence only.
export const DenominatorResolvedDataSchema = z
  .discriminatedUnion('type', [
    DemographicsCriterionSchema,
    DenominatorDiagnosisCriterionSchema,
    MedicationCriterionSchema,
    ProcedureCriterionSchema,
    LabValueCriterionSchema,
    VitalCriterionSchema,
    DeviceCriterionSchema,
    ChiefComplaintCriterionSchema,
    SettingCriterionSchema
  ])
  .superRefine(refineResolvedData);

// Tree-agnostic display code: recommendations' vocabulary is a superset of the denominator's.
export const ResolvedDataSchema = RecommendationsResolvedDataSchema;

export function resolvedDataSchemaFor(tree: PopulationCriteriaTreeKey) {
  return tree === POPULATION_CRITERIA_TREES.DENOMINATOR
    ? DenominatorResolvedDataSchema
    : RecommendationsResolvedDataSchema;
}

// ─── Statement + section schemas ────────────────────────────────────────────

export const ValidationIssueSchema = z.object({
  path: z.array(z.string()).optional(),
  message: z.string(),
  severity: z.enum(['error', 'warning']).optional()
});

export const UNRESOLVED_REASONS = {
  UNCODEABLE: 'No coded value fits this statement, so it is skipped when matching.',
  FAILED: 'The resolver did not finish. Try again.',
  SCHEMA_MISMATCH: 'The resolver returned data this criteria set cannot use.'
} as const;

/** Why the resolver declared a statement unresolvable. */
export const UNRESOLVED_CATEGORIES = {
  // A real, codable concept that needs patient data we don't collect.
  MISSING_DATA: 'missing_data',
  NO_CODE: 'no_code',
  // Workflow, consent, or clinical-judgment states no EHR field records.
  NOT_PATIENT_DATA: 'not_patient_data',
  // A change over time; the matcher compares the latest value to a threshold.
  TREND: 'trend'
} as const;

export type UnresolvedCategory = (typeof UNRESOLVED_CATEGORIES)[keyof typeof UNRESOLVED_CATEGORIES];

/** The patient data a `missing_data` statement needs and we don't collect. */
export const DATA_GAPS = {
  MEDICATION_HISTORY: 'medication_history',
  ENCOUNTER_HISTORY: 'encounter_history',
  PROCEDURE_HISTORY: 'procedure_history',
  RESULT_HISTORY: 'result_history',
  OTHER: 'other'
} as const;

export type DataGap = (typeof DATA_GAPS)[keyof typeof DATA_GAPS];

// Permissive on unknown keys so a legacy `significance` parses; the write path retires it.
export const StatementCriterionSchema = z.object({
  id: z.string(),
  statement: z.string(),
  // Coerce because the API's filterXSS middleware stringifies all primitive
  // body values; without coercion, "0.7" → schema rejection on a valid input.
  aiConfidence: z.coerce.number().min(0).max(1).nullable(),
  resolvedData: ResolvedDataSchema.nullable(),
  validationErrors: z.array(ValidationIssueSchema).optional(),
  codeValidationErrors: z.array(ValidationIssueSchema).optional(),
  // Skipped by the promotion gate. Auto-set on stale/code-rejected.
  disabled: z.boolean().optional(),
  // Why resolve produced no structured data, shown beside the disabled criterion.
  unresolvedReason: z.string().optional(),
  unresolvedCategory: z
    .enum([
      UNRESOLVED_CATEGORIES.MISSING_DATA,
      UNRESOLVED_CATEGORIES.NO_CODE,
      UNRESOLVED_CATEGORIES.NOT_PATIENT_DATA,
      UNRESOLVED_CATEGORIES.TREND
    ])
    .optional(),
  unresolvedDataGap: z
    .enum([
      DATA_GAPS.MEDICATION_HISTORY,
      DATA_GAPS.ENCOUNTER_HISTORY,
      DATA_GAPS.PROCEDURE_HISTORY,
      DATA_GAPS.RESULT_HISTORY,
      DATA_GAPS.OTHER
    ])
    .optional(),
  // A matchable statement the author can swap in for this one.
  unresolvedSuggestion: z.string().optional(),
  // Provenance. `human` means the advanced editor authored it (aiConfidence
  // is null there because there's no AI self-report to record). Downstream
  // consumers that need a trust weight (e.g. recommendations matcher)
  // treat `human` as fully trusted. Absent → 'ai' for back-compat.
  source: z.enum([CRITERION_SOURCES.AI, CRITERION_SOURCES.HUMAN]).optional()
});

// Groups nest: a group's criteria may be statements or further groups. The
// recursive z.lazy needs the explicit type annotation to close the loop.
// A type alias (not an interface) so it gets an implicit index signature and
// stays assignable to the loose walker shapes (LooseCriterion et al.).
export type CriterionGroupValue = {
  id: string;
  type: 'group';
  operator: 'AND' | 'OR';
  criteria: Array<z.infer<typeof StatementCriterionSchema> | CriterionGroupValue>;
};

export const CriterionGroupSchema: z.ZodType<CriterionGroupValue> = z.object({
  id: z.string(),
  type: z.literal('group'),
  operator: z.enum(['AND', 'OR']),
  criteria: z.array(
    z.union([StatementCriterionSchema, z.lazy(() => CriterionGroupSchema)])
  ) as z.ZodType<CriterionGroupValue['criteria']>
});

export const CriteriaItemSchema = z.union([StatementCriterionSchema, CriterionGroupSchema]);

export const CriteriaSectionSchema = z.object({
  operator: z.enum(['AND', 'OR']),
  criteria: z.array(CriteriaItemSchema)
});

export const CriteriaTreeSchema = z.object({
  inclusion: CriteriaSectionSchema,
  exclusion: CriteriaSectionSchema
});

/**
 * A tree's overview-alignment sync state: the hash of the overview text the
 * last completed check examined, and that check's verdict. Both null when the
 * tree has no authored overview section, or no check has run yet.
 */
export const OverviewSyncSchema = z.object({
  hash: z.string().nullable(),
  status: z
    .enum([
      POPULATION_CRITERIA_OVERVIEW_STATUSES.CURRENT,
      POPULATION_CRITERIA_OVERVIEW_STATUSES.NEEDS_UPDATE
    ])
    .nullable()
});

// ─── Validation helpers ─────────────────────────────────────────────────────

export type ValidationIssue = z.infer<typeof ValidationIssueSchema>;

/**
 * One criterion inside a section; the annotated shape the validators emit.
 * Loose on purpose: these shapes admit raw/legacy persisted JSONB that the
 * strict Zod schemas would reject, so walkers and validators can traverse
 * anything stored in a version row.
 */
export type CriterionShape = {
  id?: string;
  statement?: string;
  aiConfidence?: number | null;
  resolvedData?: Record<string, unknown> | null;
  validationErrors?: ValidationIssue[];
  codeValidationErrors?: ValidationIssue[];
  disabled?: boolean;
  unresolvedReason?: string;
  unresolvedCategory?: UnresolvedCategory;
  unresolvedDataGap?: DataGap;
  unresolvedSuggestion?: string;
  // Retired; only legacy persisted rows carry it.
  significance?: 'primary' | 'supporting';
  [key: string]: unknown;
};

export type CriterionGroupShape = {
  id?: string;
  type: 'group';
  operator?: 'AND' | 'OR';
  criteria: CriteriaItemShape[];
  [key: string]: unknown;
};

export type CriteriaItemShape = CriterionShape | CriterionGroupShape;

export type CriteriaSectionShape = {
  operator?: 'AND' | 'OR';
  criteria: CriteriaItemShape[];
  [key: string]: unknown;
};

export function isGroupItem(item: unknown): item is CriterionGroupShape {
  return (
    typeof item === 'object' &&
    item !== null &&
    (item as { type?: unknown }).type === 'group' &&
    Array.isArray((item as { criteria?: unknown }).criteria)
  );
}

/**
 * Depth-first walk over the leaf criteria of a section (inclusion or
 * exclusion), recursing to any group depth. The visitor receives each leaf
 * plus the id of its INNERMOST enclosing group (undefined at the top level).
 * Tolerates malformed input: a non-object section or missing criteria array
 * visits nothing. Visitors may mutate the leaves in place.
 */
export function walkCriteria(
  section: unknown,
  visit: (criterion: CriterionShape, ctx: { groupId?: string }) => void
): void {
  if (!section || typeof section !== 'object') {
    return;
  }

  const items = (section as CriteriaSectionShape).criteria;

  if (!Array.isArray(items)) {
    return;
  }

  const visitItem = (item: CriteriaItemShape, groupId?: string): void => {
    if (isGroupItem(item)) {
      for (const child of item.criteria) {
        visitItem(child, item.id ?? groupId);
      }
    } else {
      visit(item, { groupId });
    }
  };
  for (const item of items) {
    visitItem(item);
  }
}

function resolvedTypeOf(criterion: CriterionShape): string | undefined {
  const resolvedData = criterion.resolvedData;

  if (!resolvedData || typeof resolvedData !== 'object') {
    return undefined;
  }

  return (resolvedData as { type?: unknown }).type as string | undefined;
}

function isGateType(type: string | undefined): type is CriteriaGateType {
  return !!type && (CRITERIA_GATE_TYPES as readonly string[]).includes(type);
}

// Is clinical evidence REQUIRED to satisfy this item? An AND under one of these constrains every
// sibling branch, gates included. A finding inside an OR doesn't count unless every branch of that
// OR is one — otherwise the gate branch is how you satisfy it.
function requiresFinding(item: CriteriaItemShape): boolean {
  if (isGroupItem(item)) {
    return item.operator === 'OR'
      ? item.criteria.length > 0 && item.criteria.every(requiresFinding)
      : item.criteria.some(requiresFinding);
  }

  const type = resolvedTypeOf(item);

  return !!type && !isGateType(type);
}

export interface GateInOrGroupViolation {
  // The enclosing OR group's id; absent when the OR is the section itself.
  groupId?: string;
  criterionId?: string;
  statement?: string;
}

/**
 * A gate directly inside an OR would let "any adult" satisfy that branch alone. Only DIRECT
 * members of an OR count, a gate that is the sole member of its group is exempt (which is what
 * lets a lone setting exclusion save), and an OR whose branch is ANDed with a finding-bearing
 * conjunct is exempt with everything under it — there the AND still demands real evidence, so
 * `AND[ OR(ambulatory, icu), OR(abdominal pain dx, abdominal pain complaint) ]` admits nobody
 * on setting alone.
 */
export function findGateInOrGroup(section: unknown): GateInOrGroupViolation[] {
  const violations: GateInOrGroupViolation[] = [];

  if (!section || typeof section !== 'object') {
    return violations;
  }

  const checkDirectMembers = (items: CriteriaItemShape[], groupId?: string): void => {
    if (items.length <= 1) {
      return;
    }

    for (const item of items) {
      if (!isGroupItem(item) && isGateType(resolvedTypeOf(item))) {
        violations.push({ groupId, criterionId: item.id, statement: item.statement });
      }
    }
  };

  const visitLevel = (
    items: CriteriaItemShape[],
    operator: 'AND' | 'OR' | undefined,
    groupId?: string
  ): void => {
    if (operator === 'AND' && items.some(requiresFinding)) {
      return;
    }

    if (operator === 'OR') {
      checkDirectMembers(items, groupId);
    }

    for (const item of items) {
      if (isGroupItem(item)) {
        visitLevel(item.criteria, item.operator, item.id);
      }
    }
  };

  const input = section as CriteriaSectionShape;
  const items = Array.isArray(input.criteria) ? input.criteria : [];
  visitLevel(items, input.operator, undefined);

  return violations;
}

// A section with none "counts nobody": every leaf gates WHO, none describes a finding.
export function hasEnabledNonGateLeaf(section: unknown): boolean {
  let found = false;

  walkCriteria(section, criterion => {
    if (found || criterion.disabled === true) {
      return;
    }

    const type = resolvedTypeOf(criterion);

    if (type && !isGateType(type)) {
      found = true;
    }
  });

  return found;
}

function issuesFromZod(issues: z.ZodIssue[]): ValidationIssue[] {
  return issues.map(i => ({
    path: i.path.map(p => String(p)),
    message: i.message,
    severity: 'error' as const
  }));
}

/**
 * Validate a single criterion's `resolvedData` against `ResolvedDataSchema`.
 * On success, replaces `resolvedData` and `aiConfidence` with their parsed
 * (coerced) values — the api's filterXSS middleware stringifies every
 * primitive in the request body, so authors who type a number get a string
 * back unless we coerce here. Returns a new criterion object with
 * `validationErrors` populated if issues were found. Clears any pre-existing
 * `validationErrors` first — validation is deterministic on content, so
 * stale errors must not persist.
 */
export function validateCriterion(criterion: CriterionShape): CriterionShape {
  const out: CriterionShape = { ...criterion };
  delete out.validationErrors;

  if (typeof out.aiConfidence === 'string') {
    const n = Number(out.aiConfidence);

    if (!Number.isNaN(n)) {
      out.aiConfidence = n;
    }
  }

  const errors: ValidationIssue[] = [];
  const resolvedData = criterion.resolvedData;

  // Null/absent resolvedData is an unresolved (not invalid) criterion and is
  // allowed through; only present-but-malformed resolvedData is an error.
  if (resolvedData != null) {
    const parsed = ResolvedDataSchema.safeParse(resolvedData);

    if (parsed.success) {
      out.resolvedData = parsed.data as Record<string, unknown>;
    } else {
      errors.push(...issuesFromZod(parsed.error.issues));
    }
  }

  if (errors.length > 0) {
    out.validationErrors = errors;
  }

  return out;
}

/**
 * Validate an entire `CriteriaSection` (inclusion or exclusion). Walks the
 * criteria tree to any group depth and annotates each statement criterion
 * with `validationErrors`.
 */
export function validateCriteriaSection(section: unknown): {
  section: CriteriaSectionShape;
  hasErrors: boolean;
} {
  if (!section || typeof section !== 'object') {
    return {
      section: { operator: 'AND', criteria: [] },
      hasErrors: false
    };
  }

  const input = section as CriteriaSectionShape;
  const items = Array.isArray(input.criteria) ? input.criteria : [];

  let hasErrors = false;
  const validateItem = (item: CriteriaItemShape): CriteriaItemShape => {
    if (isGroupItem(item)) {
      return { ...item, criteria: item.criteria.map(validateItem) };
    }

    const validatedCriterion = validateCriterion(item);

    if (validatedCriterion.validationErrors?.length) {
      hasErrors = true;
    }

    return validatedCriterion;
  };

  return {
    section: { ...input, criteria: items.map(validateItem) },
    hasErrors
  };
}

/**
 * Reasons a criterion blocks promotion from `edit` to `live`.
 * - `validation_errors`: schema/UCUM failure from the save-time validators.
 * - `code_validation_errors`: terminology-server rejected a code (not found,
 *   ICD-9 tagged as ICD-10-CM, display mismatch).
 */
export type PromotionBlockerReason = 'validation_errors' | 'code_validation_errors';

export interface PromotionBlocker {
  section: 'inclusion' | 'exclusion';
  groupId?: string;
  criterionId?: string;
  statement?: string;
  reason: PromotionBlockerReason;
  issues?: ValidationIssue[];
}

// ─── Display view ───────────────────────────────────────────────────────────

export type ResolvedData = z.infer<typeof ResolvedDataSchema>;
export type ResolvedDataType = ResolvedData['type'];

/**
 * Uniform display shape for a single value across all criterion variants.
 * Strict `CodedValue` has `code: string`; `TextValue` and `SettingValue`
 * relax that slightly. The view widens to the loosest common shape so
 * display consumers can iterate without narrowing.
 */
export interface CodedValueView {
  display: string;
  code?: string;
  system?: string;
}

/**
 * Flat, display-ready view of a `ResolvedData`. Every field that appears on
 * any variant of the discriminated union is exposed here as a non-optional
 * field with a sensible empty default (`[]`, `null`). Use this inside UI
 * code so you don't have to narrow the variant yourself; use the strict
 * `ResolvedData` union at validation boundaries.
 */
export interface ResolvedDataView {
  type: ResolvedDataType;
  operator: 'any_of' | 'all_of' | null;
  values: readonly CodedValueView[];
  comparisons: readonly z.infer<typeof ComparisonValueSchema>[];
  ageMin: number | null;
  ageMax: number | null;
  // Always populated on demographics (defaults to 'a' if the row didn't carry
  // one — keeps display/SQL sites from re-implementing the fallback). Null on
  // non-demographics variants.
  ageUnit: AgeUnit | null;
  gender: string | null;
  // Populated on procedure (planned/active/performed) with its back-compat
  // default applied; null elsewhere, diagnosis included — a diagnosis carries
  // `scope`, not a timing.
  temporality: ProcedureTemporality | null;
  // Populated on diagnosis (encounter/problemList/any), resolved through
  // `diagnosisScopeOf` so pre-rename criteria read correctly; null elsewhere.
  scope: DiagnosisScope | null;
}

/**
 * Flatten a `ResolvedData` (discriminated union) into a `ResolvedDataView`
 * (flat shape). Always-present fields default to empty; variant-specific
 * fields (ageMin/ageMax/gender on demographics, comparisons on labValue/
 * vital) are null or `[]` on unrelated variants.
 */
export function toResolvedDataView(rd: ResolvedData): ResolvedDataView {
  // The discriminated union narrows per `type`, so the per-variant branches
  // below are exhaustive without any `as` cheating on the value side. The
  // `as CodedValueView[]` casts widen the per-variant element type
  // (CodedValue / TextValue / SettingValue) into the loosest common view
  // element — all three have compatible shape at runtime.
  const values: readonly CodedValueView[] =
    rd.type === 'demographics' || rd.type === 'labValue' || rd.type === 'vital'
      ? []
      : ((rd.values ?? []) as CodedValueView[]);

  const comparisons: readonly z.infer<typeof ComparisonValueSchema>[] =
    rd.type === 'labValue' || rd.type === 'vital' ? (rd.comparisons ?? []) : [];

  const operator: 'any_of' | 'all_of' | null =
    rd.type === 'demographics' ? null : (rd.operator ?? null);

  const temporality =
    rd.type === 'procedure' ? (rd.temporality ?? PROCEDURE_TEMPORALITY.PERFORMED) : null;

  const scope = rd.type === 'diagnosis' ? diagnosisScopeOf(rd) : null;

  const ageMin = rd.type === 'demographics' ? (rd.ageMin ?? null) : null;
  const ageMax = rd.type === 'demographics' ? (rd.ageMax ?? null) : null;
  const ageUnit: AgeUnit | null = rd.type === 'demographics' ? (rd.ageUnit ?? 'a') : null;
  const gender = rd.type === 'demographics' ? (rd.gender ?? null) : null;

  return {
    type: rd.type,
    operator,
    values,
    comparisons,
    ageMin,
    ageMax,
    ageUnit,
    gender,
    temporality,
    scope
  };
}

/**
 * Filter issues down to promote-blocking severity. Missing severity
 * defaults to 'error' (treat unknown as blocking).
 */
function blockingIssues(issues: ValidationIssue[] | undefined): ValidationIssue[] {
  if (!issues) {
    return [];
  }

  return issues.filter(i => (i.severity ?? 'error') === 'error');
}

/**
 * Walk an inclusion/exclusion pair and collect criteria that block promote:
 * those with error-severity `validationErrors` or `codeValidationErrors`.
 * Any residual warning-severity entries flow through without blocking.
 */
export function findPromotionBlockers(payload: {
  inclusion?: unknown;
  exclusion?: unknown;
}): PromotionBlocker[] {
  const blockers: PromotionBlocker[] = [];

  for (const sectionName of ['inclusion', 'exclusion'] as const) {
    const checkCriterion = (c: CriterionShape, groupId?: string) => {
      if (c.disabled === true) {
        return;
      }

      const schemaBlockers = blockingIssues(c.validationErrors);

      if (schemaBlockers.length > 0) {
        blockers.push({
          section: sectionName,
          groupId,
          criterionId: c.id,
          statement: c.statement,
          reason: 'validation_errors',
          issues: schemaBlockers
        });
        return;
      }

      const codeBlockers = blockingIssues(c.codeValidationErrors);

      if (codeBlockers.length > 0) {
        blockers.push({
          section: sectionName,
          groupId,
          criterionId: c.id,
          statement: c.statement,
          reason: 'code_validation_errors',
          issues: codeBlockers
        });
        return;
      }
    };

    walkCriteria(payload[sectionName], (c, { groupId }) => checkCriterion(c, groupId));
  }

  return blockers;
}
